import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { User as UserType } from '../types';
import { useAuthStore } from './authStore';
import { useRolesStore } from './rolesStore';

// Custom interface for Supabase users which have string IDs
interface SupabaseUser {
  id: string;
  name: string;
  email: string;
  role: string;
  phone?: string;
  isActive: boolean;
}

// Internal AppUser interface with string IDs for UUIDs
interface AppUser {
  id: string; // UUID string
  name: string;
  email: string;
  role: string;
  phone?: string;
  isActive: boolean;
  /** FK into public.roles. */
  roleId?: string;
  /** Display name of the assigned role (public.roles.name). */
  roleName?: string;
  /** Permission keys granted via the assigned role - lets other features (e.g. SiteTeamPanel) check specific grants on listed users, not just the current session user. */
  permissionKeys?: string[];
}

type RolesEmbed = { role_permissions: { permissions: { key: string } }[] } | null;

function extractPermissionKeys(rolesEmbed: unknown): string[] {
  const embed = rolesEmbed as RolesEmbed;
  return embed?.role_permissions?.map((rp) => rp.permissions.key) ?? [];
}

function extractRoleName(rolesEmbed: unknown): string | undefined {
  return (rolesEmbed as { name?: string } | null)?.name;
}

interface AddUserInput {
  name: string;
  email: string;
  phone?: string;
  password: string;
  roleId: string;
  isActive: boolean;
}

interface UpdateUserInput {
  name: string;
  email: string;
  phone?: string;
  roleId: string;
  isActive: boolean;
}

interface UsersState {
  users: AppUser[];
  projectManagers: AppUser[];
  currentUser: SupabaseUser | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;

  initialize: () => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  fetchUsers: (options?: { force?: boolean }) => Promise<void>;
  setUsers: (users: AppUser[]) => void;
  getProjectManagers: () => AppUser[];
  addUser: (userData: AddUserInput) => Promise<void>;
  updateUser: (id: string, userData: UpdateUserInput) => Promise<void>;
  toggleUserStatus: (id: string) => Promise<void>;
}

export const useUsersStore = create<UsersState>((set, get) => ({
  users: [],
  projectManagers: [],
  currentUser: null,
  loading: false,
  error: null,
  initialized: false,

  initialize: async () => {
    if (get().initialized) return;
    set({ loading: true, error: null });
    
    try {
      await get().fetchCurrentUser();
      const role = get().currentUser?.role;
      if (role === 'customer') {
        set({ initialized: true, loading: false });
        return;
      }
      await get().fetchUsers();
      set({ initialized: true, loading: false });
    } catch (error) {
      console.error('Error initializing users store:', error);
      set({ error: (error as Error).message, loading: false });
    }
  },

  fetchCurrentUser: async () => {
    try {
      // Get current authenticated user
      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError) throw authError;
      
      if (!authData.user) {
        set({ currentUser: null });
        return;
      }
      
      const userId = authData.user.id;

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id,name,email,role,phone,is_active')
        .eq('id', userId)
        .maybeSingle();

      if (userError) {
        throw userError;
      }

      if (userData) {
        const user: SupabaseUser = {
          id: userData.id,
          name: userData.name,
          email: userData.email,
          role: userData.role,
          phone: userData.phone || undefined,
          isActive: userData.is_active,
        };

        set({ currentUser: user });
        return;
      }
      
      // Fallback to auth user metadata
      const user: SupabaseUser = {
        id: authData.user.id, // UUID string from auth
        email: authData.user.email || '',
        name: authData.user.user_metadata?.name || 'User',
        role: authData.user.user_metadata?.role || 'user',
        isActive: true
      };
      
      set({ currentUser: user });
    } catch (error) {
      console.error('Error fetching current user:', error);
      set({ error: (error as Error).message });
    }
  },

  fetchUsers: async (options?: { force?: boolean }) => {
    // If users are already loaded, skip the fetch unless forced (e.g. project create modal)
    if (
      !options?.force &&
      get().users.length > 0 &&
      !get().loading
    ) {
      return;
    }
    
    set({ loading: true, error: null });
    try {
      const { data: users, error } = await supabase
        .from('users')
        .select('*,roles(name,role_permissions(permissions(key)))')
        .order('name');

      if (error) throw error;

      const formattedUsers = users.map(user => ({
        id: user.id, // Keep as string for UUID compatibility
        name: user.name,
        email: user.email,
        role: user.role,
        phone: user.phone || undefined,
        isActive: user.is_active,
        roleId: user.role_id ?? undefined,
        roleName: extractRoleName(user.roles),
        permissionKeys: extractPermissionKeys(user.roles),
      }));

      set({
        users: formattedUsers,
        projectManagers: formattedUsers.filter(
          (u) => ['project_manager', 'site_manager', 'manager', 'sales'].includes(u.role) && u.isActive
        ),
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      set({ error: (error as Error).message, loading: false });
    }
  },

  setUsers: (users) =>
    set({
      users,
      projectManagers: users.filter(
        (u) => ['project_manager', 'site_manager', 'manager', 'sales'].includes(u.role) && u.isActive
      ),
    }),

  getProjectManagers: () => get().projectManagers,

  addUser: async (userData) => {
    set({ loading: true, error: null });
    try {
      const role = useRolesStore.getState().roles.find((r) => r.id === userData.roleId);
      if (!role) throw new Error('Selected role not found');

      // First create auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            name: userData.name,
            role: role.baseRole
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Failed to create user');

      // Then create user profile. `role` (enum) is derived from the selected
      // role's base_role - RLS still gates on this column; `role_id` is the
      // real, configurable role used for app-layer permission checks.
      const { data: user, error: profileError } = await supabase
        .from('users')
        .insert([{
          id: authData.user.id,
          email: userData.email,
          name: userData.name,
          role: role.baseRole,
          role_id: userData.roleId,
          phone: userData.phone,
          is_active: true
        }])
        .select('*,roles(name,role_permissions(permissions(key)))')
        .single();

      if (profileError) throw profileError;

      const newUser = {
        id: user.id, // Keep as string for UUID compatibility
        name: user.name,
        email: user.email,
        role: user.role,
        phone: user.phone || undefined,
        isActive: user.is_active,
        roleId: user.role_id ?? undefined,
        roleName: extractRoleName(user.roles),
        permissionKeys: extractPermissionKeys(user.roles),
      };

      set((state) => {
        const users = [...state.users, newUser];
        return {
          users,
          projectManagers: users.filter(
            (u) => ['project_manager', 'site_manager', 'manager', 'sales'].includes(u.role) && u.isActive
          ),
          loading: false,
        };
      });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  updateUser: async (id, userData) => {
    set({ loading: true, error: null });
    try {
      const role = useRolesStore.getState().roles.find((r) => r.id === userData.roleId);
      if (!role) throw new Error('Selected role not found');

      const { data: user, error } = await supabase
        .from('users')
        .update({
          name: userData.name,
          email: userData.email,
          role: role.baseRole,
          role_id: userData.roleId,
          phone: userData.phone,
          is_active: userData.isActive
        })
        .eq('id', id)
        .select('*,roles(name,role_permissions(permissions(key)))')
        .single();

      if (error) throw error;

      const updatedUser = {
        id: user.id, // Keep as string for UUID compatibility
        name: user.name,
        email: user.email,
        role: user.role,
        phone: user.phone || undefined,
        isActive: user.is_active,
        roleId: user.role_id ?? undefined,
        roleName: extractRoleName(user.roles),
        permissionKeys: extractPermissionKeys(user.roles),
      };

      set((state) => {
        const users = state.users.map((u) => (u.id === id ? updatedUser : u));
        return {
          users,
          projectManagers: users.filter(
            (u) => ['project_manager', 'site_manager', 'manager', 'sales'].includes(u.role) && u.isActive
          ),
          loading: false,
        };
      });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  },

  toggleUserStatus: async (id) => {
    set({ loading: true, error: null });
    try {
      const currentUser = get().users.find(u => u.id === id);
      if (!currentUser) throw new Error('User not found');

      const { data: user, error } = await supabase
        .from('users')
        .update({
          is_active: !currentUser.isActive
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Only is_active changed - carry over the already-known role/permission
      // fields rather than dropping them (this endpoint doesn't re-embed roles).
      const updatedUser = {
        ...currentUser,
        id: user.id, // Keep as string for UUID compatibility
        name: user.name,
        email: user.email,
        role: user.role,
        phone: user.phone || undefined,
        isActive: user.is_active
      };

      set((state) => {
        const users = state.users.map((u) => (u.id === id ? updatedUser : u));
        return {
          users,
          projectManagers: users.filter(
            (u) => ['project_manager', 'site_manager', 'manager', 'sales'].includes(u.role) && u.isActive
          ),
          loading: false,
        };
      });
    } catch (error) {
      set({ error: (error as Error).message, loading: false });
    }
  }
}));